<template>
  <div class="w-full h-full px-5 mt-3">
    <h3 class="mt-0 mb-2 text-xl font-medium text-gray-600">
      {{ $t("Forecasting Objects List") }}
    </h3>
    <p class="mt-0 mb-6 leading-6">
      {{ $t("Here you can add objects that you want to be able to forecast.") }}<br>
      {{ $t("Each object will be available in the") }}
      <router-link class="underline hover:text-blue-500" to="revenue/forecast/">
        {{ $t("forecasting page") }} </router-link>.
    </p>

    <transition-group class="flex flex-wrap items-stretch gap-3 rounded-lg" tag="div" mode="in-out" name="list">
      <div v-for="forecasted_object in forecasted_objects.concat({ index: '__action' })" :key="forecasted_object.index">
        <div
          v-if="forecasted_object.index != '__action'"
          class="flex items-center justify-between w-full p-5 pb-1 space-x-6 bg-white rounded-lg shadow-md dark:bg-dark-box-background">
          <Form v-slot="{ values }" :validation-schema="schema" :initial-values="forecasted_object" @submit="save">
            <div class="flex flex-col items-start flex-1 gap-3 px-1 space-y-1 truncate !overflow-visible">
              <TextInput name="name"
                         type="text"
                         :label="$t('Name')"
                         classes="mt-2"
                         :placeholder="$t('Name')" />
              <ComboBoxInput
                :items="available_objects_to_use"
                name="object_type"
                classes="w-full relative dark:text-white"
                value-attr="id"
                label-attr="label"
                :is-box-background="true"
                :label="$t('Object')"
                :disable-input="$utils.isNotEmpty(forecasted_object?.id)"
                :disabled="$utils.isNotEmpty(forecasted_object?.id)"
                :show-label="true"
                :floating-label="false"
                :allow-null="false" />
              <ComboBoxInput
                :items="
                  $store.state.metrics.filter(
                    (x) => x.object_type == $utils.getObjectTypeById(values['object_type'])?.type && x.data_type == 'Number'
                  )
                "
                name="renewal_base_metric"
                label-attr="name"
                value-attr="id"
                classes="w-full relative dark:text-white"
                :is-box-background="true"
                :label="$t('Renewal Base')"
                :show-label="true"
                :disable-input="$utils.isNotEmpty(forecasted_object?.id)"
                :disabled="$utils.isNotEmpty(forecasted_object?.id)"
                :floating-label="false"
                :allow-null="false" />
              <ComboBoxInput
                :items="
                  $store.state.metrics.filter(
                    (x) =>
                      x.object_type == $utils.getObjectTypeById(values['object_type'])?.type &&
                      (x.data_type == 'Date' || x.data_type == 'DateTime')
                  )
                "
                name="renewal_date_metric"
                label-attr="name"
                value-attr="id"
                classes="w-full relative dark:text-white "
                :is-box-background="true"
                :disable-input="$utils.isNotEmpty(forecasted_object?.id)"
                :disabled="$utils.isNotEmpty(forecasted_object?.id)"
                :label="$t('Renewal Date')"
                :show-label="true"
                :floating-label="false"
                :allow-null="false" />

              <SelectInput
                name="forecasting_period"
                :items="[
                  { value: 'monthly', label: $t('Monthly') },
                  { value: 'quarterly', label: $t('Quarterly') },
                  { value: 'yearly', label: $t('Yearly') }
                ]"
                label-attr="label"
                :disabled="$utils.isNotEmpty(forecasted_object?.id)"
                :allow-null="false"
                :classes="'w-full'"
                value-attr="value"
                :label="$t('Forecasting Period')"
                :show-label="true"
                :is-box-background="true"
                input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" />

              <div class="py-0.5 text-xs font-medium justify-end overflow-visible">
                <Field v-slot="{ handleChange }" name="is_using_arr">
                  <SwitchGroup as="div" class="flex items-end justify-between">
                    <span class="flex flex-col flex-grow">
                      <SwitchLabel as="span"
                                   class="text-sm font-medium text-gray-900 dark:text-white"
                                   passive>{{ $t("ARR/MRR") }}
                      </SwitchLabel>
                      <SwitchDescription as="span" class="text-sm text-common-grey">
                        {{ $t("Do you use ARR or MRR as your primary measurement?.") }}</SwitchDescription>
                    </span>
                    <div class="flex items-center pl-2 space-x-1 dark:text-white">
                      <span :class="[!forecasted_object.is_using_arr ? 'opacity-100' : 'opacity-0']">{{ $t("MRR") }}</span>
                      <Switch
                        :disabled="$utils.isNotEmpty(forecasted_object?.id)"
                        :value="forecasted_object.is_using_arr ? 't' : 'f'"
                        :class="[
                          forecasted_object.is_using_arr ? 'bg-blue' : 'bg-gray-200',
                          'disabled:opacity-70 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                        ]"
                        @update:model-value="
                          () => {
                            forecasted_object.is_using_arr = !(forecasted_object.is_using_arr == 't' ? true : false) ? 't' : 'f'
                            handleChange(forecasted_object.is_using_arr)
                          }
                        ">
                        <span
                          aria-hidden="true"
                          :class="[
                            forecasted_object.is_using_arr && $store.state.portfolio.ui_options?.language == 'ar'
                              ? '-translate-x-5'
                              : forecasted_object.is_using_arr
                                ? 'translate-x-5'
                                : 'translate-x-0',
                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                          ]" />
                      </Switch>
                      <span :class="[forecasted_object.is_using_arr ? 'opacity-100' : 'opacity-0']">{{ $t("ARR") }}</span>
                    </div>
                  </SwitchGroup>
                </Field>
              </div>
            </div>
            <div class="flex justify-between w-full mt-auto overflow-visible divide-gray-200">
              <MultipleActionsSubmitButton
                class="w-[160px] m-2 mt-3 mr-0 cancel-btn"
                :default-action-index="2"
                :actions="[
                  {
                    name: $t('ReSync'),
                    onClick: () => resyncObject(forecasted_object),
                    icon: RotateCw
                  },
                  {
                    name: $t('Take Snapshot'),
                    onClick: () => takeSnapshot(forecasted_object),
                    icon: Camera
                  },
                  {
                    name: $t('Reset'),
                    onClick: () => resetObject(forecasted_object),
                    icon: Undo2
                  },
                  {
                    name: $t('Delete'),
                    onClick: () => removeObject(forecasted_object),
                    icon: Trash2
                  }
                ]" />
              <button
                :disabled="
                  !isForecastedObjectChanged({ ...forecasted_object, ...values }) ||
                    !isForecastedObjectValid({ ...forecasted_object, ...values })
                "
                class="w-1/4 m-2 mt-3 mr-0 primary-btn"
                type="submit">
                {{ $t("Save") }}
              </button>
            </div>
          </Form>
        </div>
        <div
          v-else
          class="flex items-center justify-center w-full h-full p-20 m-0 bg-white rounded-lg justify-self-stretch dark:bg-dark-box-background hover:bg-gray-200 dark:hover:opacity-80 hover:cursor-pointer"
          @click="addObject">
          <span class="flex items-center justify-center font-semibold dark:text-white">
            <Plus class="w-10 h-10" />
            {{ $t("Add Object to Forecast") }}
          </span>
        </div>
      </div>
    </transition-group>
    <Modal v-model:is-open="show_object_delete_confirm_modal" max-width="max-w-[33%] mx-5" default-success-msg="Success">
      <template #form>
        <h3 class="text-2xl font-medium leading-10 text-center text-gray-900 capitalize dark:text-white">
          <span>{{ $t("Delete Forecasting Object") }} </span>
        </h3>

        <div class="px-4 pb-4 mt-2 mb-2 bg-white rounded-lg dark:bg-dark-screen-background dark:text-white sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <div class="mt-2">
                <p class="text-red-500">
                  {{ $t("Deleting the") }}
                  <span class="font-semibold capitalize">{{ to_be_deleted_object.name }} {{ $t("forecasting object") }}</span>
                  {{ $t("will permanently remove it from the system. This action is irreversible.") }}
                </p>
                <p class="text-gray-500">
                  {{ $t("Do you wish to proceed with the deletion of") }}
                  <span class="font-semibold capitalize">{{ to_be_deleted_object.name }}</span>
                  {{ $t("from forecasting") }}?
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-white bg-red-400 border border-gray-300 rounded-md shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="confirmObjectDelete">
            <span>{{ $t("Yes") }}</span>
          </button>

          <button
            type="button"
            class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm dark:bg-dark-box-background dark:text-white dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="show_object_delete_confirm_modal = false">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>
    <Modal v-model:is-open="show_object_reset_confirm_modal" max-width="max-w-[33%] mx-5" :default-success-msg="$t('Success')">
      <template #form>
        <h3 class="text-2xl font-medium leading-10 text-center text-gray-900 capitalize dark:text-white">
          <span>{{ $t("Reset Forecasting Object") }} </span>
        </h3>

        <div class="px-4 pb-4 mt-2 mb-2 bg-white rounded-lg dark:bg-dark-screen-background dark:text-white sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <div class="mt-2">
                <p class="text-red-500">
                  {{ $t("Resetting will revert all associated forecasts for") }}
                  <strong>{{ $t("you") }}</strong> {{ $t("and") }} <strong>{{ $t("all of the users") }}</strong>
                  {{ $t("to their original values. Any record with empty or null base") }}
                  {{ $t("values will be omitted.") }}
                </p>
                <p class="text-gray-500">
                  {{ $t("Are you sure you want to reset the") }}
                  <span class="font-semibold capitalize">{{ to_be_reset_object.name }} {{ $t("forecasting object") }}</span>?
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-white bg-red-400 border border-gray-300 rounded-md shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="confirmObjectReset">
            <span>{{ $t("Yes, Reset") }}</span>
          </button>

          <button
            type="button"
            class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm dark:bg-dark-box-background dark:text-white dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="show_object_reset_confirm_modal = false">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>
    <Modal v-model:is-open="show_object_resync_confirm_modal" max-width="max-w-[33%] mx-5" :default-success-msg="$t('Success')">
      <template #form>
        <h3 class="text-2xl font-medium leading-10 text-center text-gray-900 capitalize dark:text-white">
          {{ $t("Resync Forecasting Object") }}
        </h3>

        <div class="px-4 pb-4 mt-2 mb-2 bg-white rounded-lg dark:bg-dark-screen-background dark:text-white sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <div class="mt-2">
                <p class="text-red-500">
                  {{ $t("Resyncing will update the") }}
                  <span class="font-semibold capitalize">{{ to_be_resynced_object.name }} {{ $t("forecasting object") }}</span>
                  {{ $t("by adding any missing forecasts not previously recorded.") }}
                </p>
                <p class="text-gray-500">
                  {{ $t("Would you like to proceed with the resync of") }}
                  <span class="font-semibold capitalize">{{ to_be_resynced_object.name }}</span>?
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-white bg-red-400 border border-gray-300 rounded-md shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="confirmObjectResync">
            {{ $t("Yes, Resync") }}
          </button>

          <button
            type="button"
            class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm dark:bg-dark-box-background dark:text-white dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="show_object_resync_confirm_modal = false">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>
    <Modal v-model:is-open="show_object_take_snapshot_confirm_modal" max-width="max-w-[33%] mx-5" :default-success-msg="$t('Success')">
      <template #form>
        <h3 class="text-2xl font-medium leading-10 text-center text-gray-900 capitalize dark:text-white">
          {{ $t("Take Snapshot Forecasting Object") }}
        </h3>

        <div class="px-4 pb-4 mt-2 mb-2 bg-white rounded-lg dark:bg-dark-screen-background dark:text-white sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <div class="mt-2">
                <p class="text-gray-500">
                  {{ $t("Would you like to proceed with the take snapshot of") }}
                  <span class="font-semibold capitalize">{{ snapshot_target_object.name }}</span>?
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-white bg-red-400 border border-gray-300 rounded-md shadow-sm hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="confirmObjectTakeSnapshot">
            {{ $t("Yes, Take Snapshot") }}
          </button>

          <button
            type="button"
            class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm dark:bg-dark-box-background dark:text-white dark:border-dark-divider-border hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-light sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            @click="show_object_take_snapshot_confirm_modal = false">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import * as Yup from "yup"
import { Form, Field } from "vee-validate"
import Modal from "@/components/shared/Modal.vue"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import MultipleActionsSubmitButton from "@/components/shared/MultipleActionsSubmitButton.vue"
import TextInput from "@/components/shared/TextInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import { Switch, SwitchDescription, SwitchGroup, SwitchLabel } from "@headlessui/vue"
import { Plus, Trash2, RotateCw, Undo2, Camera } from "lucide-vue-next"
import { MODULES } from "@/store/constants/references"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { markRaw } from "vue"

export default {
  name: "ForecastingPreferences",
  components: {
    SelectInput,
    Form,
    Field,
    TextInput,
    Modal,
    ComboBoxInput,
    MultipleActionsSubmitButton,
    Plus,
    Switch,
    SwitchDescription,
    SwitchGroup,
    SwitchLabel
  },
  inject: ["isModuleActivated", "isModuleVisible", "updateLayouts"],
  data() {
    return {
      Camera: markRaw(Camera),
      RotateCw: markRaw(RotateCw),
      Undo2: markRaw(Undo2),
      Trash2: markRaw(Trash2),
      schema: {},
      to_be_deleted_object: null,
      show_object_delete_confirm_modal: false,
      to_be_reset_object: null,
      show_object_reset_confirm_modal: false,
      to_be_resynced_object: null,
      show_object_resync_confirm_modal: false,
      snapshot_target_object: null,
      show_object_take_snapshot_confirm_modal: false,
      forecasted_objects: [],
      forecasted_objects_clone: []
    }
  },
  computed: {
    available_objects_to_use() {
      // let used = this.forecasted_objects.map((o) => o.object_type?.type)
      // return this.$store.state.object_types.filter((ot) => !used.includes(ot?.type))
      return this.$store.state.object_types
    }
  },

  activated() {
    this.initSettings()
  },
  created() {
    this.initSettings()
  },
  methods: {
    initSettings() {
      let forecasting_mappings =
        utils.find(this.$store.state.settings.modules_settings, {
          reference_code: "forecasting_mappings"
        })?.forecasting_mappings || []
      this.forecasted_objects = []
      forecasting_mappings.forEach((mapping, index) => {
        this.forecasted_objects.push({
          index: index,
          organization: mapping.organization,
          name: mapping.name,
          id: mapping.id,
          object_type: mapping.object_type,
          is_using_arr: mapping.is_using_arr ? "t" : "f",
          forecasting_period: mapping.forecasting_period,
          renewal_base_metric: mapping.renewal_base_metric,
          renewal_date_metric: mapping.renewal_date_metric
        })
      })
      this.schema = Yup.object().shape({
        name: Yup.string().required(this.$t("name is required")).nullable(),
        is_using_arr: Yup.mixed().oneOf(["t", "f"]),
        forecasting_period: Yup.mixed().oneOf(["monthly", "quarterly", "yearly"]),
        renewal_base_metric: Yup.string().required(this.$t("metric is required")).nullable(),
        renewal_date_metric: Yup.string().required(this.$t("metric is required")).nullable(),
        object_type: Yup.string().required(this.$t("object type is required")).nullable()
      })

      this.forecasted_objects_clone = this.$utils.clone(this.forecasted_objects)
    },
    resyncObject(forecasted_object) {
      this.to_be_resynced_object = forecasted_object
      this.show_object_resync_confirm_modal = true
    },
    confirmObjectResync() {
      this.show_object_resync_confirm_modal = false
      this.$store.dispatch("settings/resyncForecasting", this.to_be_resynced_object.id)
      this.$toast.open({
        message: this.$t("Forecast") + ` ${this.to_be_resynced_object.name} ` + this.$t("resync started"),
        position: "top",
        type: "success",
        duration: 3000
      })
    },
    takeSnapshot(forecasted_object) {
      this.snapshot_target_object = forecasted_object
      this.show_object_take_snapshot_confirm_modal = true
    },
    confirmObjectTakeSnapshot() {
      this.show_object_take_snapshot_confirm_modal = false
      this.$store.dispatch("settings/takeSnapshotForecasting", this.snapshot_target_object.id)
      this.$toast.open({
        message: this.$t("Taking a snapshot for forecast") + ` ${this.snapshot_target_object.name} ` + this.$t("has started"),
        position: "top",
        type: "success",
        duration: 3000
      })
    },
    resetObject(forecasted_object) {
      this.to_be_reset_object = forecasted_object
      this.show_object_reset_confirm_modal = true
    },
    confirmObjectReset() {
      this.show_object_reset_confirm_modal = false
      this.$store.dispatch("settings/resetForecasting", this.to_be_reset_object.id)
      this.$toast.open({
        message: this.$t("Forecast") + ` ${this.to_be_reset_object.name} ` + this.$t("reset started"),
        position: "top",
        type: "success",
        duration: 3000
      })
    },

    removeObject(obj) {
      this.to_be_deleted_object = null
      if (obj?.id) {
        this.to_be_deleted_object = obj
        this.show_object_delete_confirm_modal = true
      } else {
        this.forecasted_objects = this.forecasted_objects.filter((x) => x.index != obj.index)
      }
    },
    confirmObjectDelete() {
      this.forecasted_objects = this.forecasted_objects.filter((x) => x.id != this.to_be_deleted_object.id)
      this.$store.dispatch("settings/deleteForecasting", this.to_be_deleted_object.id)
      let forecasting_pages = this.$store.state.portfolio.pages_layout["forecasting"]?.filter(
        (p) => p.name != this.to_be_deleted_object.name
      )
      this.$emitter.emit(ALL_EMITTERS.PAGES_DELETED_IN_SIDE_MENU, {
        pages_deleted: this.$store.state.portfolio.pages_layout["forecasting"]?.filter((p) => p.name == this.to_be_deleted_object.name),
        page_ref: "forecasting"
      })
      this.updateLayouts(forecasting_pages, "forecasting")

      // this.$store.commit("settings/setSetting", {
      //   reference_code: "forecasting_mappings",
      //   forecasting_mappings: this.forecasted_objects
      // })
      this.show_object_delete_confirm_modal = false
      this.$emitter.emit(ALL_EMITTERS.DELETE_FORECAST, this.to_be_deleted_object.id)
      this.$toast.open({
        message: this.$t("Forecast") + ` ${this.to_be_deleted_object.name} ` + this.$t("has been removed from forecasting"),
        position: "top",
        type: "success",
        duration: 3000
      })
    },
    addObject() {
      if (!this.isModuleActivated(MODULES.MULTIPLE_FORECASTING_OBJECTS) && this.forecasted_objects.length == 1) {
        this.$emitter.emit(ALL_EMITTERS.MODULE_IS_NOT_ACTIVATED, MODULES.MULTIPLE_FORECASTING_OBJECTS)
      } else {
        if (this.available_objects_to_use.length > 0) {
          this.forecasted_objects.push({
            name: this.$t(this.available_objects_to_use[0].label) + " " + this.$t("Forecast"),
            index: this.forecasted_objects.length + 1,
            object_type: this.available_objects_to_use[0].id,
            is_using_arr: "t",
            renewal_base_metric: null,
            renewal_date_metric: null,
            organization: this.$store.state.organization.id
          })
        }
      }
    },
    isForecastedObjectChanged(forecasted_object, option = null) {
      if (option) {
        let initial_forecasted_object = this.forecasted_objects_clone.find((item) => item.index == forecasted_object.index)
        return !initial_forecasted_object || !this.$utils.isEqual(forecasted_object[option], initial_forecasted_object[option])
      } else {
        let initial_forecasted_object = this.forecasted_objects_clone.find((item) => item.index == forecasted_object.index)
        return !initial_forecasted_object || !this.$utils.isEqual(forecasted_object, initial_forecasted_object)
      }
    },
    isForecastedObjectValid(forecasted_object) {
      return (
        forecasted_object.object_type &&
        forecasted_object.renewal_base_metric &&
        forecasted_object.renewal_date_metric &&
        utils.isNotEmpty(forecasted_object.name)
      )
    },

    save(forecast) {
      forecast["target"] = true
      this.$utils.replaceItem(this.forecasted_objects, forecast, "index")

      this.$store
        .dispatch("settings/setSetting", {
          reference_code: "forecasting_mappings",
          forecasting_mappings: this.forecasted_objects
        })
        .then((res) => {
          res["index"] = forecast.index
          res["is_using_arr"] = forecast.is_using_arr
          this.$utils.replaceItem(this.forecasted_objects, res, "index")
          this.$emitter.emit(ALL_EMITTERS.REFRESH_FORECAST, res)
        })
      this.$store.commit("settings/setSetting", {
        reference_code: "forecasting_mappings",
        forecasting_mappings: this.forecasted_objects
      })
      this.forecasted_objects_clone = this.$utils.clone(this.forecasted_objects)

      this.$toast.open({
        message:
          this.$t("Generating forecast for object") +
          ` <strong>${utils.getObjectTypeById(forecast.object_type).label}</strong> (${forecast.name})`,
        position: "top",
        type: "success",
        duration: 3000
      })
    }
  }
}
</script>

<style>
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
